/**
 * SPA-aware navigation. On Capacitor, a bundled WebView serves only
 * ``index.html`` from ``webDir`` — any ``location.href = "/projects"``
 * triggers a real fetch the local server can't satisfy and 404s.
 * Use ``history.pushState`` + a synthetic ``popstate`` so React
 * Router's ``createBrowserHistory`` re-renders without a network hop.
 *
 * On web/desktop we keep the original ``location.href`` semantics so
 * Django-rendered pages (``/user/login/``, ``/logout``) still get a
 * full reload — those are not React routes.
 */

const onCapacitor = (): boolean =>
  typeof window !== "undefined" &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof (window as any).Capacitor !== "undefined";

/**
 * Sanitize a ``?next=`` deep-link target before navigating to it.
 * ``spaNavigate`` is a hard ``window.location`` call on web, so an
 * absolute URL — or ``/\evil.com``, which browsers normalize to the
 * protocol-relative ``//evil.com`` — would be an open redirect.
 * Accept only same-origin relative paths; anything else → fallback.
 */
export const safeNextPath = (next: string | null | undefined, fallback = "/"): string => {
  if (!next) return fallback;
  let dest: string;
  try {
    dest = decodeURIComponent(next);
  } catch {
    return fallback;
  }
  if (!dest.startsWith("/") || dest.startsWith("//") || dest.includes("\\")) return fallback;
  // Anonymous-only surfaces are never meaningful post-login
  // destinations — a stale ``?next=/verify-receipt`` (from a 401
  // bounce) made every login land on the verify page instead of home.
  if (!isMeaningfulNext(dest)) return fallback;
  return dest;
};

/** Paths that must never be a post-login destination: auth pages, logout,
 *  anonymous-only surfaces. A ``?next=/logout`` (left behind when a sign-out
 *  bounced through the login wall) made every sign-in log the user straight
 *  out again — the "login again and again" loop. */
export const isMeaningfulNext = (path: string): boolean => {
  const p = path.split("?")[0];
  if (/^\/(auth|verify-receipt)(\/|$)/.test(p)) return false;
  if (/^\/(user\/)?(logout|login|signup)\/?$/.test(p)) return false;
  return true;
};

/** ``?next=`` value for the current location, or "" when the current page is
 *  not a meaningful destination (so callers send the user Home instead). */
export const currentNextParam = (): string => {
  if (typeof window === "undefined") return "";
  const path = `${window.location.pathname}${window.location.search}`;
  return isMeaningfulNext(path) && window.location.pathname !== "/" ? encodeURIComponent(path) : "";
};

export const spaNavigate = (path: string, replace = false): void => {
  if (typeof window === "undefined") return;
  if (onCapacitor()) {
    // Prefer the router's own history object (exposed by App.jsx):
    // a synthetic PopStateEvent is sometimes missed by the router on
    // a cold stack — the URL changed to "/" after login but nothing
    // re-rendered until the user tapped a menu item ("blank page").
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const h = (window as any).__afriRouterHistory;
    if (h && typeof h.replace === "function") {
      if (replace) h.replace(path);
      else h.push(path);
      return;
    }
    if (replace) window.history.replaceState({}, "", path);
    else window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }
  if (replace) window.location.replace(path);
  else window.location.href = path;
};
